import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatInput, useChatInputControls} from 'llm/chat-library/ChatInput';
import {ChatMessageThread} from 'llm/chat-library/ChatMessageThread';
import ChatScrollContainer from 'llm/chat-library/ChatScrollContainer';
import {SideBySideLayout} from 'llm/chat-library/SideBySideLayout';
import {doesChatTypeShowProgress, mapAgentModeToAgentType} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useArtifact} from 'llm/chat-library/useArtifact';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {DraftIndicatorButton} from 'llm/data-capture/DraftIndicatorButton';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as modals from 'util/Modals';

export interface ChatProps {
  chatSessionId: string;
  chatType: ChatType;
  isExpanded: boolean;
  locked?: boolean;
}

/**
 * Generic chat component that supports multiple chat types (Deep Research, Compose, etc.).
 *
 * This component uses the artifact abstraction to manage outputs like draft documents,
 * and can show progress UI for chat types that support tool calls.
 */
export function Chat({chatSessionId, chatType, isExpanded, locked = false}: ChatProps): JSX.Element {
  const {getFileUploads, ...controls} = useChatInputControls(chatType);
  const {inputText, setInputText, selectedFiles, setSelectedFiles, agentMode, publicDataEnabled} = controls;
  const [hasTrackedChatOpen, setHasTrackedChatOpen] = useState<boolean>(false);

  const {
    events,
    isLoading,
    errorMessage,
    isAssistantRunning,
    isLastMessageFromUser,
    sendMessage,
    progressData,
    latestApplicationState,
  } = useMessageThread({
    chatSessionId,
    includeToolCalls: doesChatTypeShowProgress(chatType),
    disableRealtimeUpdates: locked,
  });
  const [latestUserMessageInfo, setLatestUserMessageInfo] = useState<LatestUserMessageInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Use the artifact hook - handles document artifacts from application state
  const {activeArtifact, isPhoneArtifactOpen, openArtifact, closePhoneArtifact} = useArtifact({
    latestApplicationState,
    isExpanded,
  });

  // On phone-sized screens, we show the chat by default and have an option to open the artifact.
  const isPhoneWidth = useIsPhoneWidth();

  // Track opening of AI chat (only once per session)
  useEffect(() => {
    if (!isLoading && !hasTrackedChatOpen) {
      // TODO(BNCH-133493): openedNewChat is always false - also this should be invoked for all chat types.
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_LLM_CHAT, {
        chatType,
        messageCount: events.filter((event) => event.__typename === 'DisplayMessageChatEventNode').length,
        openedNewChat: Boolean(!chatSessionId),
      });
      setHasTrackedChatOpen(true);
    }
  }, [chatSessionId, chatType, isLoading, hasTrackedChatOpen, events]);

  const handleSubmitMessage = useCallback(async () => {
    if (!agentMode) {
      // This is unexpected, but just in case, notify the user why their message was not sent.
      notifications.error('An agent type must be selected before sending a message', {
        id: 'chat-agent-mode-required',
      });
      return;
    }

    const message = inputText;
    const fileUploads = selectedFiles.map((file) => {
      assert(file.completedUpload != null, 'Unexpected submit with files still uploading.');
      return file.completedUpload;
    });

    // If we need to retry, show a confirmation modal
    if (isLastMessageFromUser) {
      const confirmed = await modals.confirm(
        'Alert',
        'The previous message encountered an error and will be removed. Do you want to continue?',
        ButtonStyle.PRIMARY,
        {
          acceptText: 'Continue',
          rejectText: 'Cancel',
          promiseCompatible: true,
          enableRedesign: true,
        }
      );

      if (!confirmed) {
        return;
      }
    }

    setInputText('');
    setSelectedFiles([]);
    setIsSubmitting(true);

    try {
      // Wait for artifact saves before sending
      await activeArtifact?.beforeSendMessage?.();

      await sendMessage({
        text: message,
        fileUploads,
        agentType: mapAgentModeToAgentType(agentMode),
        publicDataEnabled,
        retry: isLastMessageFromUser,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    activeArtifact,
    agentMode,
    inputText,
    setInputText,
    selectedFiles,
    setSelectedFiles,
    sendMessage,
    isLastMessageFromUser,
    publicDataEnabled,
  ]);

  // If we have an error message, we may or may not have any past chat history to show. If there is
  // no past chat history (e.g. error on initial pageload), just show an error for the whole chat.
  // Otherwise, we will render the events we have below with a staleness message.
  if (errorMessage != null && events.length === 0) {
    return (
      <div
        className={css(styles.errorBannerContainer)}
        {...getTestProps({component: 'Chat', element: 'error-banner-container'})}
      >
        <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
      </div>
    );
  }

  const messageThreadElement =
    // events may be non-empty while loading due to optimistic messages
    events.length === 0 && (isLoading || isAssistantRunning) ? (
      <CenteredHorizontalThrobber {...getTestProps({component: 'Chat', element: 'loading-throbber'})} />
    ) : (
      <ChatMessageThread
        events={events}
        chatSessionId={chatSessionId}
        onUpdateLatestUserMessageInfo={setLatestUserMessageInfo}
        actionButton={
          activeArtifact ? (
            <DraftIndicatorButton onClick={openArtifact} artifactType={activeArtifact.artifactType} />
          ) : null
        }
        readonly={false}
        progressData={progressData}
      />
    );

  const chatContent = (
    <ChatScrollContainer
      chatMessagesElement={messageThreadElement}
      chatInputElement={
        locked ? null : (
          <ChatInput
            chatSessionId={chatSessionId}
            {...controls}
            onSubmit={handleSubmitMessage}
            inputDisabled={isAssistantRunning || isSubmitting || errorMessage != null}
            allowedChatTypes={[chatType]}
          />
        )
      }
      latestUserMessageInfo={latestUserMessageInfo}
      autoScroll={true}
    />
  );

  const isEditable = !isAssistantRunning && !locked && !isSubmitting;

  let content;
  if (isPhoneWidth) {
    // Mobile: show chat by default, or artifact when the user has opened it.
    if (isPhoneArtifactOpen && activeArtifact) {
      const ArtifactComponent = activeArtifact.Component;
      // TODO: ideally the component with the close is wrapping the artifact, i.e.
      // not a concern of a given artifact component.
      content = (
        <ArtifactComponent data={activeArtifact.data} editable={isEditable} onClose={closePhoneArtifact} />
      );
    } else {
      content = chatContent;
    }
  } else if (isExpanded && activeArtifact) {
    // Desktop expanded view with artifact: show side-by-side chat and artifact.
    const ArtifactComponent = activeArtifact.Component;
    content = (
      <SideBySideLayout
        chatContent={chatContent}
        artifactContent={<ArtifactComponent data={activeArtifact.data} editable={isEditable} />}
      />
    );
  } else {
    // Desktop collapsed and non-artifact view: show chat only
    content = chatContent;
  }

  return (
    <div className={css(styles.contentContainer)}>
      {errorMessage != null && !isSubmitting && !isAssistantRunning && (
        <div
          className={css(styles.errorBannerContainer)}
          {...getTestProps({component: 'Chat', element: 'error-banner-with-chat'})}
        >
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`${errorMessage} Showing previously-loaded chat.`}
          />
        </div>
      )}
      {content}
    </div>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 0,
    flex: 1,
  },
  errorBannerContainer: {
    margin: '16px 16px 0',
  },
});
